import { useCallback, useEffect, useState } from 'react';

/**
 * localStorage에 값을 영속화하는 useState.
 * 컴포넌트가 마운트될 때 저장된 값을 읽어 복원하고, 값이 바뀔 때마다 자동 저장한다.
 * 검사 진행 상황(섹션별 current/answers)을 기기에 보존해, 중간에 화면이 리셋돼도
 * "이어서 풀기"로 복원할 수 있도록 한다.
 */
export function usePersistentState<T>(storageKey: string, initial: T) {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw) as T;
    } catch {
      // 파싱 실패 시 초기값 사용
    }
    return initial;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // 저장 실패는 무시 (프라이빗 모드 등)
    }
  }, [storageKey, state]);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // 무시
    }
  }, [storageKey]);

  return [state, setState, reset] as const;
}